// React
import { memo } from "react";

// Servisni sloj
import { useHandleDateChange, useHandleDropdownChange, useHandleTextFieldChange } from "services/forms/hooks";

// UI
import { DatePicker } from "ui/components/DatePicker";
import ContactDropdown from "ui/components/Dropdown/ContactDropdown/ContactDropdown";
import { TextField } from "ui/components/TextField/TextField";

import InvoiceIncomingSupplierTooltip from "./components/InvoiceIncomingSupplierTooltip";
import { PAYMENT_METHOD_LABELS } from "./consts";
import { InvoiceIncomingHeaderProps } from "./types";

/**
 * Zaglavlje ulaznog računa — sve što treba da se račun provjeri i plati: tko ga je izdao,
 * na što se odnosi i kamo ide uplata. Kartica je pregled, pa su polja zaključana.
 */
const InvoiceIncomingHeader: React.FC<InvoiceIncomingHeaderProps> = (props) => {
  const { formValues, setFormValues } = props;

  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);
  const handleDateChange = useHandleDateChange(setFormValues, formValues);
  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);

  const isForeignCurrency = !!formValues.currencyId && formValues.currencyId.toLowerCase() !== "eur";
  const paymentMethodLabel = formValues.paymentMethodId
    ? PAYMENT_METHOD_LABELS[formValues.paymentMethodId] ?? formValues.paymentMethodId
    : "";

  return (
    <div className="flex flex-col gap-3 mb-3">
      <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Račun</div>

      <div className="flex gap-4">
        <div className="flex-1">
          {/* Dobavljač otvara redak računa — prvo tko ga je izdao, pa broj i datum. */}
          <ContactDropdown
            name="contactSupplierId"
            label="Dobavljač"
            value={formValues.contactSupplierId}
            onChange={handleDropdownChange}
            tooltip={
              <InvoiceIncomingSupplierTooltip
                name={formValues.supplierName}
                address={formValues.supplierAddress}
                oib={formValues.supplierOIB}
                vat={formValues.supplierVAT}
                iban={formValues.supplierIBAN}
              />
            }
            disabled
          />
        </div>
        <div className="flex-1">
          <TextField
            type="text"
            name="invoiceNumber"
            label="Broj računa"
            value={formValues.invoiceNumber}
            onChange={handleTextFieldChange}
            disabled
          />
        </div>
        <div className="flex-1">
          <DatePicker
            name="invoiceDate"
            label="Datum računa"
            value={formValues.invoiceDate}
            onChange={handleDateChange}
            disabled
          />
        </div>
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          <DatePicker
            name="dueDate"
            label="Datum dospijeća"
            value={formValues.dueDate}
            onChange={handleDateChange}
            disabled
          />
        </div>
        <div className="flex-1">
          <DatePicker
            name="deliveryDate"
            label="Datum isporuke"
            value={formValues.deliveryDate}
            onChange={handleDateChange}
            disabled
          />
        </div>
        <div className="flex-1">
          <TextField
            type="text"
            name="currencyId"
            label="Valuta"
            value={formValues.currencyId?.toUpperCase() ?? ""}
            onChange={handleTextFieldChange}
            disabled
          />
        </div>
      </div>

      {isForeignCurrency && (
        <div className="flex gap-4">
          <div className="flex-1">
            <TextField
              type="text"
              name="exchangeRate"
              label="Tečaj"
              value={formValues.exchangeRate ?? ""}
              onChange={handleTextFieldChange}
              disabled
            />
          </div>
          <div className="flex-1"></div>
          <div className="flex-1"></div>
        </div>
      )}

      {/* Plaćanje je vlastita cjelina — odvojena linijom kao sekcije na ugovoru. */}
      <div className="border-t border-slate-200 pt-4 px-4 -mx-4 -mb-1"></div>

      <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Plaćanje</div>

      <div className="flex gap-4">
        <div className="flex-1">
          <TextField
            type="text"
            name="paymentMethodLabel"
            label="Način plaćanja"
            value={paymentMethodLabel}
            onChange={handleTextFieldChange}
            disabled
          />
        </div>
        <div className="flex-1">
          <TextField
            type="text"
            name="paymentReference"
            label="Poziv na broj"
            value={formValues.paymentReference}
            onChange={handleTextFieldChange}
            disabled
          />
        </div>
        <div className="flex-1">
          <TextField
            type="text"
            name="durationDays"
            label="Rok plaćanja (dana)"
            value={formValues.durationDays ?? ""}
            onChange={handleTextFieldChange}
            disabled
          />
        </div>
      </div>
    </div>
  );
};

export default memo(InvoiceIncomingHeader);
